/**
 * ApplicationsIndexPage
 *
 * Cross-listing index of non-withdrawn applicants across active listings.
 * Route: /leasing/applications
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '../../components/ui/skeleton';
import {
  fetchApplications,
  type ApplicationItem,
} from '../../lib/leasing.api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

const ROLE_LABELS: Record<ApplicationItem['role'], string> = {
  primary: 'Primary',
  co_applicant: 'Co-applicant',
  guarantor: 'Guarantor',
};

const STATUS_CLASSES: Record<ApplicationItem['status'], string> = {
  invited: 'bg-muted text-muted-foreground',
  started: 'bg-warning/15 text-warning',
  submitted: 'bg-success/15 text-success',
};

const STATUS_LABELS: Record<ApplicationItem['status'], string> = {
  invited: 'Invited',
  started: 'Started',
  submitted: 'Submitted',
};

// ---------------------------------------------------------------------------
// Application row
// ---------------------------------------------------------------------------

function ApplicationRow({ item }: { item: ApplicationItem }) {
  const roleCls = 'bg-muted text-muted-foreground';
  const statusCls = STATUS_CLASSES[item.status];

  return (
    <li data-testid={`leasing-application-row-${item.applicantId}`}>
      <Link
        to={`/leasing/leads/${item.leadId}`}
        aria-label={`Open lead for ${item.applicantName}`}
        className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="min-w-0 flex-1">
          {/* Line 1: applicant name + role chip + status chip. AC-L10: the applicant name
              (this row's subject) and the lead's prospect name/email below it can be two
              different people (e.g. a co-applicant row on a lead started by someone else) —
              label which is which. */}
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-xs text-muted-foreground shrink-0">Applicant:</span>
            <span className="font-medium text-foreground">{item.applicantName}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${roleCls}`}>
              {ROLE_LABELS[item.role]}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusCls}`}>
              {STATUS_LABELS[item.status]}
            </span>
          </div>
          {/* Line 2: prospect name / email (the lead this application belongs to) */}
          <p className="text-xs text-muted-foreground">
            <span className="shrink-0">Lead:</span>{' '}
            {item.prospectName ?? item.prospectEmail}
            {item.prospectName && ` · ${item.prospectEmail}`}
          </p>
          {/* Line 3: listing address */}
          <p className="text-xs text-muted-foreground mt-0.5">{item.listingAddress}</p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatAge(item.lastActivityAt)}
        </span>
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ApplicationsIndexPage() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['leasing-applications'],
    queryFn: fetchApplications,
    staleTime: 60_000,
  });

  const items = data?.items ?? [];

  return (
    <div
      data-testid="leasing-applications-page"
      className="max-w-3xl mx-auto pb-6 px-[var(--page-padding-x)] pt-6"
    >
      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <div className="py-4 text-center space-y-2">
          <p className="text-sm text-muted-foreground">Unable to load applications. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="text-sm text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">No active applications.</p>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <ul data-testid="applications-list" className="space-y-2">
          {items.map((item) => (
            <ApplicationRow key={item.applicantId} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
}
